import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

export const APPROACHES = {
  "cbt": {
    title: "Cognitive Behavioural Therapy (CBT)",
    intro: [
      "Cognitive Behavioural Therapy is one of the most researched forms of psychotherapy. It is built on a simple, well-supported idea: the way we think about a situation shapes how we feel and what we do — and those patterns can be examined and changed.",
      "CBT is structured and goal-oriented. Sessions focus on identifying unhelpful thought patterns, testing them against evidence, and practising alternative responses between sessions, so progress continues outside the therapy room.",
    ],
    helps: ["Anxiety and constant worry", "Depression and low mood", "Panic attacks", "Stress and burnout", "Negative self-talk", "Sleep difficulties"],
    steps: [
      ["Map the pattern", "Together we identify the thoughts, feelings, and behaviours that keep the difficulty going."],
      ["Test and reframe", "You learn to examine unhelpful thoughts against real evidence and build balanced alternatives."],
      ["Practise between sessions", "Structured exercises consolidate new responses until they become your default."],
    ],
  },
  "cpt": {
    title: "Cognitive Processing Therapy (CPT)",
    intro: [
      "Cognitive Processing Therapy is a leading evidence-based treatment for post-traumatic stress and the aftermath of traumatic experiences. It focuses on 'stuck points' — beliefs formed during or after trauma that keep the nervous system on alert.",
      "CPT follows a clear protocol, typically over 12 structured sessions. The work is paced and collaborative: you remain in control of what is shared and when, throughout.",
    ],
    helps: ["PTSD and trauma symptoms", "Childhood or developmental trauma", "Sexual violence and abuse", "Domestic abuse", "Guilt and self-blame after trauma", "Feeling unsafe or on edge"],
    steps: [
      ["Understand the impact", "We map how the trauma has shaped beliefs about safety, trust, power, and worth."],
      ["Work the stuck points", "Structured writing and discussion help examine and shift beliefs that keep you stuck."],
      ["Consolidate", "New, balanced beliefs are strengthened so the past stops steering the present."],
    ],
  },
  "dbt": {
    title: "Dialectical Behaviour Therapy (DBT)",
    intro: [
      "Dialectical Behaviour Therapy was developed for intense emotions — the kind that feel unmanageable and lead to reactions you later regret. It balances two things at once: accepting yourself as you are, and building the skills to change.",
      "DBT is highly practical. Its four skill sets — mindfulness, distress tolerance, emotion regulation, and interpersonal effectiveness — give you concrete tools for the moments that usually derail you.",
    ],
    helps: ["Intense or rapidly shifting emotions", "Relationship conflict and reactivity", "Impulsive coping behaviours", "Self-criticism and shame", "Feeling chronically overwhelmed", "Difficulty tolerating distress"],
    steps: [
      ["Stabilize", "Learn mindfulness and distress-tolerance skills to get through hard moments safely."],
      ["Regulate", "Build the ability to name, understand, and influence your emotional responses."],
      ["Relate", "Practise asking for what you need and holding boundaries without damaging connection."],
    ],
  },
  "act": {
    title: "Acceptance & Commitment Therapy (ACT)",
    intro: [
      "Acceptance and Commitment Therapy starts from a different premise than most approaches: struggling against difficult thoughts and feelings often makes them stronger. ACT teaches psychological flexibility instead — making room for inner experience while moving toward the life you actually want.",
      "Sessions combine mindfulness, acceptance strategies, and values clarification. The goal is not to eliminate discomfort, but to stop it from making your decisions for you.",
    ],
    helps: ["Chronic anxiety and overthinking", "Feeling stuck or directionless", "Avoidance that shrinks your life", "Perfectionism", "Life transitions", "Stress and burnout"],
    steps: [
      ["Notice without fighting", "Learn to observe thoughts and feelings without being hooked by them."],
      ["Clarify values", "Identify what genuinely matters to you — the direction, not just the destination."],
      ["Commit to action", "Take concrete, values-led steps even when discomfort shows up alongside them."],
    ],
  },
  "gottman": {
    title: "Gottman Method Couples Therapy",
    intro: [
      "The Gottman Method is built on more than four decades of research observing thousands of couples — what makes relationships stabilize, and what predicts their breakdown. It is one of the most studied approaches to couples therapy in the world.",
      "Work begins with a thorough assessment of the relationship, then targets the specific patterns found: how conflict starts, how it escalates, and how repair happens. At Haya Healing Psychotherapy, this framework is applied with particular attention to the cultural and family contexts South Asian, Muslim, and intercultural couples carry.",
    ],
    helps: ["Recurring conflict that never resolves", "Communication breakdown", "Trust and betrayal recovery", "Emotional distance and disconnection", "In-law and extended family dynamics", "Premarital preparation"],
    steps: [
      ["Assess thoroughly", "A structured assessment maps the relationship's strengths and pressure points before treatment begins."],
      ["Change the conflict pattern", "Learn how to raise issues gently, de-escalate, and repair — practised live in session."],
      ["Rebuild friendship and meaning", "Strengthen the fondness, respect, and shared purpose that durable relationships run on."],
    ],
  },
  "humanistic": {
    title: "Humanistic Therapy",
    intro: [
      "Humanistic therapy places you — not a diagnosis or a protocol — at the centre of the work. It holds that people move naturally toward growth when they are met with genuine understanding, acceptance, and honesty.",
      "Sessions are client-centred and non-directive in spirit: your therapist follows your lead while helping you hear yourself more clearly, strengthen self-worth, and reconnect with your own judgment.",
    ],
    helps: ["Low self-worth and self-doubt", "Identity questions", "Feeling unseen or unheard", "Loss of direction or meaning", "Difficulty trusting your own judgment", "Emotional numbness"],
    steps: [
      ["Be fully heard", "Experience a relationship where nothing about you needs to be performed or defended."],
      ["Reconnect with yourself", "Clarify what you actually feel and want, beneath expectation and obligation."],
      ["Grow from your own ground", "Change that comes from self-understanding tends to hold."],
    ],
  },
  "psychodynamic": {
    title: "Psychodynamic Therapy",
    intro: [
      "Psychodynamic therapy looks beneath the presenting problem to the patterns underneath it — many of them formed early, outside conscious awareness, and repeated across relationships without our choosing.",
      "This is exploratory work. Sessions examine recurring themes, defences, and early relational experiences, using the therapy relationship itself as live information about how you relate to others.",
    ],
    helps: ["Repeating the same relationship patterns", "Long-standing anxiety or low mood", "Unresolved family history", "Difficulty with intimacy or trust", "Self-sabotage", "A persistent sense that something is 'off'"],
    steps: [
      ["Notice the pattern", "Bring recurring themes — in relationships, work, and self-talk — into clear view."],
      ["Understand its origins", "Trace how early experiences shaped the defences you carry now."],
      ["Choose differently", "Awareness creates room for responses that are actually yours."],
    ],
  },
  "trauma-informed": {
    title: "Trauma-Informed Care",
    intro: [
      "Trauma-informed care is not a single technique — it is the foundation every session at Haya Healing Psychotherapy is built on. It recognizes that trauma is common, that it affects the mind, body, and behaviour, and that treatment must never recreate the conditions of harm.",
      "In practice, this means safety and choice come first: pacing is negotiated, consent is ongoing, and you are never pushed to disclose more than you are ready to hold.",
    ],
    helps: ["Any concern where past trauma plays a role", "Feeling unsafe in your own body", "Hypervigilance or shutdown", "Medical or institutional trauma", "Cultural and intergenerational trauma", "First-time therapy after difficult experiences"],
    steps: [
      ["Establish safety", "A predictable, transparent frame — you always know what is happening and why."],
      ["Work at your pace", "Processing begins only when your nervous system has the capacity for it."],
      ["Integrate", "New experiences of safety in the room generalize to life outside it."],
    ],
  },
  "relational": {
    title: "Relational Therapy",
    intro: [
      "Relational therapy starts from the observation that we are shaped in relationship — and wounded in relationship — so healing happens in relationship too. The connection between client and therapist is not just the container for the work; it is part of the work itself.",
      "Sessions pay close attention to attachment patterns: how you reach for connection, how you protect yourself from it, and how those moves show up with partners, family, and in the therapy room.",
    ],
    helps: ["Attachment anxiety or avoidance", "Difficulty trusting or depending on others", "Fear of abandonment", "Loneliness within relationships", "People-pleasing and over-functioning", "Recurring relational conflict"],
    steps: [
      ["Map your attachment style", "Understand how you learned to connect — and to protect yourself from connection."],
      ["Experience a secure relationship", "The therapy relationship becomes a place to practise new ways of relating."],
      ["Carry it outward", "New relational patterns are tested and strengthened in your life."],
    ],
  },
  "play-therapy": {
    title: "Play Therapy",
    intro: [
      "Play is a child's natural language. Play therapy uses it clinically: toys, art, stories, and games become the medium through which children express what they cannot yet say in words, and through which change happens at their developmental level.",
      "Work with children at Haya Healing Psychotherapy is collaborative with parents — you are involved in intake, periodic check-ins, and strategies at home, while your child gets a space that belongs to them.",
    ],
    helps: ["Big feelings and emotional regulation", "Anxiety and worries", "Behavioural concerns", "Social skills and friendships", "School difficulties", "Family changes — separation, moves, new siblings"],
    steps: [
      ["Build the relationship", "Sessions begin with safety and play — trust before technique."],
      ["Express through play", "Your child externalizes and works through feelings in the medium they know best."],
      ["Generalize to home and school", "Skills and themes from the room are carried into daily life with parent support."],
    ],
  },
};

export default function ApproachDetail() {
  const { slug } = useParams();
  const a = APPROACHES[slug];

  useEffect(() => {
    if (!a) return;
    document.title = `${a.title} in Milton & Burlington | Haya Healing Psychotherapy`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", `${a.title} at Haya Healing Psychotherapy — evidence-based care in English & Urdu. In person in Milton & Burlington, virtual across Ontario.`);
  }, [a]);

  if (!a) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="px-6 md:px-14 py-32 max-w-2xl">
          <p className="eyebrow">Approach</p>
          <h1 className="font-serif-display text-4xl mt-4">Page not found.</h1>
          <a href="/#approach" className="pill-btn pill-outline mt-8">View all approaches</a>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="px-6 md:px-14 pt-14 md:pt-20 pb-12 max-w-4xl">
        <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/#approach" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
          <ArrowLeft size={14} /> All approaches
        </motion.a>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={1} className="eyebrow mt-8">Clinical Approach · Milton, Burlington &amp; Online Across Ontario</motion.p>
        <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={2} data-testid="approach-heading"
          className="font-serif-display mt-4 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.1] font-normal">
          {a.title}
        </motion.h1>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-2xl">
          {a.intro.map((p) => <p key={p}>{p}</p>)}
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-8">
          <a data-testid="approach-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book Initial Consultation <ArrowRight size={15} /></a>
        </motion.div>
      </section>
      <section data-testid="approach-helps" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10 bg-[#f1ece4]/50">
        <Reveal><p className="eyebrow">What it helps with</p></Reveal>
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-3.5 mt-8 max-w-4xl">
          {a.helps.map((b, i) => (
            <Reveal key={b} i={i % 4} className="flex gap-3 text-[#2c1f20]/80">
              <span className="text-[#8a5a63] mt-0.5">✦</span><span>{b}</span>
            </Reveal>
          ))}
        </div>
      </section>
      <section data-testid="approach-steps" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10">
        <Reveal><p className="eyebrow">How the work unfolds</p></Reveal>
        <div className="grid sm:grid-cols-3 gap-6 mt-10">
          {a.steps.map(([t, d], i) => (
            <Reveal key={t} i={i + 1} className="card">
              <p className="font-serif-display text-3xl text-[#8a5a63]">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-xl md:text-2xl mt-3 leading-snug">{t}</h3>
              <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="px-6 md:px-14 py-10 border-t border-[#2c1f20]/10">
        <p className="text-xs text-[#2c1f20]/50 leading-relaxed max-w-5xl">
          {a.title} at Haya Healing Psychotherapy — serving Milton, Burlington, Oakville, Mississauga, Brampton, Halton Region, and the Greater Toronto Area. Care led by Momna Sajid, Registered Psychotherapist (Qualifying), CRPO. Sessions in English and Urdu. In-person at Halton Medix Walk-In Clinic (Milton &amp; Burlington) and virtual across Ontario.
        </p>
      </section>
      <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/50">
        <Reveal><p className="eyebrow">Begin here</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">Book a <em className="text-[#6e4550]">complimentary initial consultation.</em></h2></Reveal>
        <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
          <a data-testid="approach-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
          <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
          <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
        </Reveal>
        <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
      </footer>
    </div>
  );
}
